import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faSave, faTimes, faSpinner,
  faTicketAlt, faPercent, faCheckCircle, faTimesCircle, faCalendar, faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';

interface Coupon {
  id: number;
  code: string;
  discount_percent: number;
  description?: string;
  is_active: boolean;
  usage_limit?: number;
  usage_count: number;
  actual_usage_count?: number;
  valid_from?: string;
  valid_until?: string;
  created_at: string;
  created_by_email?: string;
}

const AdminCoupons: React.FC = () => {
  const { user } = useAuth();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_percent: 10,
    description: '',
    is_active: true,
    usage_limit: '',
    valid_from: '',
    valid_until: ''
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await api.get('/coupons/admin');
      setCoupons(response.data.coupons || []);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_percent: 10,
      description: '',
      is_active: true,
      usage_limit: '',
      valid_from: '',
      valid_until: ''
    });
    setEditingCoupon(null);
    setShowForm(false);
  };

  const handleEdit = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      discount_percent: coupon.discount_percent,
      description: coupon.description || '',
      is_active: coupon.is_active,
      usage_limit: coupon.usage_limit?.toString() || '',
      valid_from: coupon.valid_from ? new Date(coupon.valid_from).toISOString().slice(0, 16) : '',
      valid_until: coupon.valid_until ? new Date(coupon.valid_until).toISOString().slice(0, 16) : ''
    });
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: any = {
        code: formData.code.trim(),
        discount_percent: formData.discount_percent,
        description: formData.description.trim() || null,
        is_active: formData.is_active,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null
      };

      if (editingCoupon) {
        await api.put(`/coupons/admin/${editingCoupon.id}`, payload);
      } else {
        await api.post('/coupons/admin', payload);
      }

      resetForm();
      fetchCoupons();
    } catch (error: any) {
      console.error('Failed to save coupon:', error);
      alert(error.response?.data?.error || 'Failed to save coupon');
    }
  };

  const handleDelete = async (couponId: number) => {
    const coupon = coupons.find(c => c.id === couponId);
    if (!coupon) return;

    if (!window.confirm(`Are you sure you want to delete coupon "${coupon.code}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(couponId);
      await api.delete(`/coupons/admin/${couponId}`);
      fetchCoupons();
    } catch (error: any) {
      console.error('Failed to delete coupon:', error);
      alert(error.response?.data?.error || 'Failed to delete coupon');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (coupon: Coupon) => {
    if (!coupon.valid_until) return false;
    return new Date(coupon.valid_until) < new Date();
  };

  const isNotYetValid = (coupon: Coupon) => {
    if (!coupon.valid_from) return false;
    return new Date(coupon.valid_from) > new Date();
  };

  if (loading) {
    return (
      <div className="gp-page flex items-center justify-center">
        <div className="text-center">
          <div className="gp-spinner mb-3" aria-hidden="true" />
          <p className="text-sm gp-text-muted">Loading coupons...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gp-page py-6">
      <div className="gp-container">
        <Link to="/dashboard" className="gp-back-link mb-4">
          <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
          Back to Dashboard
        </Link>

        <div className="gp-page-header">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="gp-icon-tile">
                  <FontAwesomeIcon icon={faTicketAlt} aria-hidden="true" />
                </span>
                <h1 className="gp-page-title">Coupon Management</h1>
              </div>
              <p className="gp-page-subtitle">Create and manage discount coupons</p>
            </div>
            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="gp-btn-primary"
            >
              <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
              <span>New Coupon</span>
            </button>
          </div>
        </div>

        {showForm && (
          <div className="gp-modal-backdrop" onClick={resetForm}>
            <div className="gp-modal max-w-2xl p-5" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold" style={{ color: 'var(--gp-ink)' }}>
                  {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
                </h2>
                <button
                  type="button"
                  onClick={resetForm}
                  className="gp-text-muted hover:opacity-80"
                  aria-label="Close"
                >
                  <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
                </button>
              </div>

              <form onSubmit={handleSave} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--gp-ink)' }}>
                    Coupon Code *
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="input-field"
                    placeholder="SAVE20"
                    required
                    disabled={!!editingCoupon}
                  />
                  <p className="text-xs gp-text-muted mt-1">
                    Code will be automatically converted to uppercase
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--gp-ink)' }}>
                    Discount Percentage *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={formData.discount_percent}
                      onChange={(e) => setFormData({ ...formData, discount_percent: parseInt(e.target.value) || 0 })}
                      className="input-field"
                      required
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 gp-text-muted text-sm">%</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--gp-ink)' }}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input-field"
                    rows={3}
                    placeholder="Optional description for this coupon"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--gp-ink)' }}>
                      Usage Limit
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.usage_limit}
                      onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                      className="input-field"
                      placeholder="Leave empty for unlimited"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--gp-ink)' }}>
                      Status
                    </label>
                    <select
                      value={formData.is_active ? 'active' : 'inactive'}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                      className="gp-select w-full"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--gp-ink)' }}>
                      Valid From
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.valid_from}
                      onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--gp-ink)' }}>
                      Valid Until
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.valid_until}
                      onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                      className="input-field"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-3">
                  <button type="button" onClick={resetForm} className="gp-btn-ghost">
                    Cancel
                  </button>
                  <button type="submit" className="gp-btn-primary">
                    <FontAwesomeIcon icon={faSave} aria-hidden="true" />
                    <span>{editingCoupon ? 'Update' : 'Create'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {coupons.length === 0 ? (
          <div className="gp-card text-center py-10">
            <FontAwesomeIcon icon={faTicketAlt} className="text-3xl gp-text-muted mb-3" aria-hidden="true" />
            <p className="text-sm gp-text-muted mb-4">No coupons found</p>
            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="gp-btn-primary"
            >
              Create Your First Coupon
            </button>
          </div>
        ) : (
          <div className="gp-card p-0 overflow-hidden">
            <div className="gp-table-wrap border-0 rounded-none">
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Discount</th>
                    <th>Usage</th>
                    <th>Validity</th>
                    <th>Status</th>
                    <th className="!text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon) => (
                    <tr key={coupon.id}>
                      <td>
                        <div className="font-semibold text-sm" style={{ color: 'var(--gp-ink)' }}>{coupon.code}</div>
                        {coupon.description && (
                          <div className="text-xs gp-text-muted">{coupon.description}</div>
                        )}
                      </td>
                      <td>
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold"
                          style={{ background: 'var(--gp-brand-soft)', color: 'var(--gp-brand)' }}
                        >
                          <FontAwesomeIcon icon={faPercent} className="text-[10px]" aria-hidden="true" />
                          {coupon.discount_percent}%
                        </span>
                      </td>
                      <td className="text-sm">
                        {coupon.actual_usage_count || coupon.usage_count}
                        {coupon.usage_limit ? ` / ${coupon.usage_limit}` : ' / ∞'}
                      </td>
                      <td>
                        <div className="flex flex-col">
                          {coupon.valid_from && (
                            <span className="text-xs gp-text-muted">
                              From: {formatDate(coupon.valid_from)}
                            </span>
                          )}
                          {coupon.valid_until && (
                            <span
                              className="text-xs"
                              style={{ color: isExpired(coupon) ? '#b91c1c' : 'var(--gp-ink-muted)' }}
                            >
                              Until: {formatDate(coupon.valid_until)}
                            </span>
                          )}
                          {!coupon.valid_from && !coupon.valid_until && (
                            <span className="text-xs gp-text-muted">No expiration</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {isExpired(coupon) ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold"
                            style={{ background: '#fef2f2', color: '#b91c1c' }}
                          >
                            <FontAwesomeIcon icon={faTimesCircle} className="text-[10px]" aria-hidden="true" />
                            Expired
                          </span>
                        ) : isNotYetValid(coupon) ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold"
                            style={{ background: 'var(--gp-accent-soft)', color: 'var(--gp-accent)' }}
                          >
                            <FontAwesomeIcon icon={faCalendar} className="text-[10px]" aria-hidden="true" />
                            Not Yet Valid
                          </span>
                        ) : coupon.is_active ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold"
                            style={{ background: 'var(--gp-success-soft)', color: 'var(--gp-success)' }}
                          >
                            <FontAwesomeIcon icon={faCheckCircle} className="text-[10px]" aria-hidden="true" />
                            Active
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold"
                            style={{ background: 'var(--gp-surface-muted)', color: 'var(--gp-ink-muted)' }}
                          >
                            <FontAwesomeIcon icon={faTimesCircle} className="text-[10px]" aria-hidden="true" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="!text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleEdit(coupon)}
                            className="p-1.5 rounded-md transition-colors"
                            style={{ color: 'var(--gp-brand)' }}
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faEdit} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(coupon.id)}
                            disabled={deleting === coupon.id}
                            className="p-1.5 rounded-md transition-colors disabled:opacity-50"
                            style={{ color: '#b91c1c' }}
                            title="Delete"
                          >
                            {deleting === coupon.id ? (
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin" aria-hidden="true" />
                            ) : (
                              <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCoupons;
